import { useTranslations } from '@/i18n/translations';
import { Field, Input, Row, Select, Stack, TagInput, Textarea } from '@leanin/ui';
import { REGIONS, REGION_LABELS, type Region } from '@leanin/shared/region';

// The editable profile fields for a Network — name, tagline, region, mission,
// description, welcome, join instructions, tags, and social links. Shared by
// the admin Overview edit surface (NetworkOverviewSection) and the create
// flow (/networks/create) so the two never drift. Image upload, visibility,
// and the save/publish lifecycle are owned by each consumer; this component
// is a controlled view over the text content only.

export type NetworkSocialDraft = {
  website: string;
  linkedin: string;
  instagram: string;
  facebook: string;
  twitter: string;
  email: string;
};

export type NetworkProfileDraft = {
  name: string;
  tagline: string;
  region: Region;
  mission: string;
  description: string;
  welcomeMessage: string;
  joinInstructions: string;
  tags: string[];
  social: NetworkSocialDraft;
};

export const EMPTY_SOCIAL_DRAFT: NetworkSocialDraft = {
  website: '',
  linkedin: '',
  instagram: '',
  facebook: '',
  twitter: '',
  email: '',
};

type Props = {
  readonly value: NetworkProfileDraft;
  readonly onChange: (patch: Partial<NetworkProfileDraft>) => void;
};

export function NetworkProfileFields({ value, onChange }: Props) {
  const tn = useTranslations('Networks');

  const setSocial = (patch: Partial<NetworkSocialDraft>) =>
    onChange({ social: { ...value.social, ...patch } });

  return (
    <>
      <Stack p="lg" gap="lg" style={{ borderBottom: '1px solid var(--border)' }}>
        <Row gap="md" wrap>
          <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
            <Field label={tn('profileFields.name')} required>
              <Input
                value={value.name}
                onChange={(e) => onChange({ name: e.target.value })}
                maxLength={120}
              />
            </Field>
          </Stack>
          <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
            <Field label={tn('profileFields.tagline')}>
              <Input
                value={value.tagline}
                onChange={(e) => onChange({ tagline: e.target.value })}
                placeholder={tn('profileFields.taglinePlaceholder')}
                maxLength={240}
              />
            </Field>
          </Stack>
        </Row>
        <Row gap="md" wrap align="flex-start">
          <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0, maxWidth: 320 }}>
            <Field label={tn('profileFields.region')} description={tn('profileFields.regionHelp')}>
              <Select
                value={value.region}
                options={REGIONS.map((r) => ({
                  value: r,
                  label: REGION_LABELS[r],
                }))}
                onChange={(e) => onChange({ region: e.target.value as Region })}
              />
            </Field>
          </Stack>
        </Row>
      </Stack>

      <Stack p="xl" gap="lg">
        <Field label={tn('profileFields.mission')}>
          <Textarea
            value={value.mission}
            onChange={(e) => onChange({ mission: e.target.value })}
            rows={2}
            placeholder={tn('profileFields.missionPlaceholder')}
            maxLength={1000}
          />
        </Field>

        <Field label={tn('profileFields.description')}>
          <Textarea
            value={value.description}
            onChange={(e) => onChange({ description: e.target.value })}
            rows={5}
            maxLength={2000}
          />
        </Field>

        <Field
          label={tn('profileFields.welcome')}
          description={tn('profileFields.welcomeHelp')}
        >
          <Textarea
            value={value.welcomeMessage}
            onChange={(e) => onChange({ welcomeMessage: e.target.value })}
            rows={6}
            maxLength={4000}
            placeholder={tn('profileFields.welcomePlaceholder')}
          />
        </Field>

        <Field
          label={tn('profileFields.howToJoin')}
          description={tn('profileFields.howToJoinHelp')}
        >
          <Textarea
            value={value.joinInstructions}
            onChange={(e) => onChange({ joinInstructions: e.target.value })}
            rows={4}
            maxLength={4000}
            placeholder={tn('profileFields.howToJoinPlaceholder')}
          />
        </Field>

        <Field>
          <Field.Label>{tn('profileFields.tags')}</Field.Label>
          <TagInput
            value={value.tags}
            onChange={(tags) => onChange({ tags })}
            placeholder={tn('profileFields.tagPlaceholder')}
            addLabel={tn('profileFields.addTag')}
            removeLabel={(tag) => tn('profileFields.removeTagAria', { tag })}
          />
        </Field>

        <Field
          label={tn('profileFields.socialLinks')}
          description={tn('profileFields.socialLinksHelp')}
        >
          <Field.Group>
            <Stack gap="sm">
              <Row gap="md" wrap>
                <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <Field label={tn('profileFields.website')}>
                    <Input
                      value={value.social.website}
                      onChange={(e) => setSocial({ website: e.target.value })}
                      placeholder={tn('profileFields.websitePlaceholder')}
                    />
                  </Field>
                </Stack>
                <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <Field label={tn('profileFields.linkedin')}>
                    <Input
                      value={value.social.linkedin}
                      onChange={(e) => setSocial({ linkedin: e.target.value })}
                      placeholder={tn('profileFields.linkedinPlaceholder')}
                    />
                  </Field>
                </Stack>
              </Row>
              <Row gap="md" wrap>
                <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <Field label={tn('profileFields.instagram')}>
                    <Input
                      value={value.social.instagram}
                      onChange={(e) => setSocial({ instagram: e.target.value })}
                      placeholder={tn('profileFields.instagramPlaceholder')}
                    />
                  </Field>
                </Stack>
                <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <Field label={tn('profileFields.facebook')}>
                    <Input
                      value={value.social.facebook}
                      onChange={(e) => setSocial({ facebook: e.target.value })}
                      placeholder={tn('profileFields.facebookPlaceholder')}
                    />
                  </Field>
                </Stack>
              </Row>
              <Row gap="md" wrap>
                <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <Field label={tn('profileFields.twitter')}>
                    <Input
                      value={value.social.twitter}
                      onChange={(e) => setSocial({ twitter: e.target.value })}
                      placeholder={tn('profileFields.twitterPlaceholder')}
                    />
                  </Field>
                </Stack>
                <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <Field label={tn('profileFields.email')}>
                    <Input
                      type="email"
                      value={value.social.email}
                      onChange={(e) => setSocial({ email: e.target.value })}
                      placeholder={tn('profileFields.emailPlaceholder')}
                    />
                  </Field>
                </Stack>
              </Row>
            </Stack>
          </Field.Group>
        </Field>
      </Stack>
    </>
  );
}

// Maps a draft's social block to the strict, blank-stripped object the server
// accepts (SocialLinksSchema rejects empty strings). Shared by both consumers.
export function socialDraftToPayload(
  social: NetworkSocialDraft,
): Partial<Record<keyof NetworkSocialDraft, string>> {
  const out: Partial<Record<keyof NetworkSocialDraft, string>> = {};
  if (social.website.trim()) out.website = social.website.trim();
  if (social.linkedin.trim()) out.linkedin = social.linkedin.trim();
  if (social.instagram.trim()) out.instagram = social.instagram.trim();
  if (social.facebook.trim()) out.facebook = social.facebook.trim();
  if (social.twitter.trim()) out.twitter = social.twitter.trim();
  if (social.email.trim()) out.email = social.email.trim();
  return out;
}
